import React from 'react';
import { FormField } from '../../../../SmartDataTable/types';
import {
  SearchSelectField,
  AutoCompleteField,
  CustomAutoCompleteField,
  TelephoneField,
  DateTimePickerField,
} from '@src/components/shared/fields';
import {
  InputField,
  TextAreaField,
  NumberField,
  SelectField,
  CheckboxField,
  SwitchField,
  FileField,
  MarkdownField,
  TableField,
  RadioField,
  SearchTermTagField,
} from '@src/components/shared/fields';

interface FormFieldRendererProps {
  field: FormField;
  form?: any;
}

// Enhanced validation utility function for table fields
const validateTableField = (
  field: FormField,
  value: any[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!field.required) {
    return { isValid: true, errors: [] };
  }

  if (!value || value.length === 0) {
    if (field.tableProps?.minRows && field.tableProps.minRows > 0) {
      errors.push(
        `${field.label} must have at least ${field.tableProps.minRows} row(s)`
      );
    } else {
      errors.push(`${field.label} is required`);
    }
    return { isValid: false, errors };
  }

  // Check minimum rows requirement
  if (field.tableProps?.minRows && value.length < field.tableProps.minRows) {
    errors.push(
      `${field.label} must have at least ${field.tableProps.minRows} row(s)`
    );
    return { isValid: false, errors };
  }

  // Validate each row with enhanced validation
  const invalidRowIndices: number[] = [];

  value.forEach((row: any, rowIndex: number) => {
    if (!field.tableProps?.columns) return;

    // Check if the row has any data
    const hasAnyData = field.tableProps.columns.some((column: any) => {
      const cellValue = row[column.dataIndex];
      if (cellValue === null || cellValue === undefined || cellValue === '') {
        return false;
      }
      // For resourceSelect fields, check if value property exists (for object format)
      // or if it's a simple ID value (for table storage format)
      if (typeof cellValue === 'object' && cellValue.hasOwnProperty('value')) {
        return (
          cellValue.value !== null &&
          cellValue.value !== undefined &&
          cellValue.value !== ''
        );
      }
      // For simple ID values (as stored in table rows), just check if it's truthy
      return !!cellValue;
    });

    // If the row is completely empty, consider it valid (user hasn't started filling it yet)
    if (!hasAnyData) {
      return;
    }

    // If the row has some data, validate all required fields with enhanced validation
    const hasInvalidFields = field.tableProps.columns.some((column: any) => {
      if (column.required || column.field?.required) {
        const cellValue = row[column.dataIndex];
        if (cellValue === null || cellValue === undefined || cellValue === '') {
          return true;
        }
        // For resourceSelect fields, check if value property exists (for object format)
        // or if it's a simple ID value (for table storage format)
        if (
          typeof cellValue === 'object' &&
          cellValue.hasOwnProperty('value')
        ) {
          return (
            cellValue.value === null ||
            cellValue.value === undefined ||
            cellValue.value === ''
          );
        }
        // For simple ID values (as stored in table rows), just check if it's truthy
        return !cellValue;
      }
      return false;
    });

    // Additional validation for specific field types
    const hasValidationErrors = field.tableProps.columns.some((column: any) => {
      if (column.field?.validation) {
        const cellValue = row[column.dataIndex];
        const validation = column.field.validation;

        // Pattern validation
        if (validation.pattern && typeof cellValue === 'string') {
          const regex = new RegExp(validation.pattern);
          if (!regex.test(cellValue)) {
            return true;
          }
        }

        // Min validation for numbers
        if (validation.min !== undefined && typeof cellValue === 'number') {
          if (cellValue < validation.min) {
            return true;
          }
        }

        // Max validation for numbers
        if (validation.max !== undefined && typeof cellValue === 'number') {
          if (cellValue > validation.max) {
            return true;
          }
        }
      }
      return false;
    });

    if (hasInvalidFields || hasValidationErrors) {
      invalidRowIndices.push(rowIndex + 1); // +1 for 1-based row numbering
    }
  });

  if (invalidRowIndices.length > 0) {
    errors.push(
      `${field.label} has validation errors in row(s): ${invalidRowIndices.join(', ')}`
    );
  }

  return { isValid: errors.length === 0, errors };
};

// Validation utility function for resourceSelect fields
const validateSearchSelectValue = (value: any): boolean => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  // Handle SearchSelect value format { label, value, data }
  if (typeof value === 'object' && value.hasOwnProperty('value')) {
    return (
      value.value !== null && value.value !== undefined && value.value !== ''
    );
  }

  // For simple ID values, just check if it's truthy
  return !!value;
};

// Get validation rules for a field
const getValidationRules = (field: FormField) => {
  const rules = [];

  // Required validation
  if (field.required) {
    rules.push({
      validator: async (rule: any, value: any) => {
        let isValid = true;
        let errorMessage = `${field.label} is required`;

        if (field.type === 'table') {
          const result = validateTableField(field, value);
          isValid = result.isValid;
          if (!isValid && result.errors.length > 0) {
            errorMessage = result.errors[0];
          }
        } else if (field.type === 'resourceSelect') {
          isValid = validateSearchSelectValue(value);
        } else {
          // Basic validation for other field types
          if (value === null || value === undefined) {
            isValid = false;
          } else if (typeof value === 'string') {
            isValid = value.trim() !== '';
          } else if (Array.isArray(value)) {
            isValid = value.length > 0;
          } else if (typeof value === 'object') {
            if (value.hasOwnProperty('value')) {
              isValid =
                value.value !== null &&
                value.value !== undefined &&
                value.value !== '';
            } else {
              isValid = Object.keys(value).length > 0;
            }
          } else {
            isValid = !!value;
          }
        }

        if (!isValid) {
          throw new Error(errorMessage);
        }
      },
      validateTrigger: ['onBlur', 'onSubmit'],
    });
  }

  // Custom validator
  if (field.validation?.validator) {
    rules.push({
      validator: field.validation.validator,
      validateTrigger: ['onBlur', 'onSubmit'],
    });
  }

  // Pattern validation
  if (field.validation?.pattern) {
    rules.push({
      pattern: new RegExp(field.validation.pattern),
      message: field.validation.message || `${field.label} format is invalid`,
      validateTrigger: ['onBlur', 'onSubmit'],
    });
  }

  // Min validation
  if (field.validation?.min !== undefined) {
    rules.push({
      min: field.validation.min,
      message: `${field.label} must be at least ${field.validation.min}`,
      validateTrigger: ['onBlur', 'onSubmit'],
    });
  }

  // Max validation
  if (field.validation?.max !== undefined) {
    rules.push({
      max: field.validation.max,
      message: `${field.label} must be at most ${field.validation.max}`,
      validateTrigger: ['onBlur', 'onSubmit'],
    });
  }

  return rules;
};

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  field,
  form,
}) => {
  const {
    name,
    type,
    placeholder,
    defaultValue,
    options,
    props = {},
    searchable = true,
    align,
    format,
    decimalPlaces,
    disabled,
  } = field;

  const fieldName = name.includes('.') ? name.split('.') : name;

  // Custom handling for markdown and file fields
  if (type === 'markdown' && form) {
    return (
      <MarkdownField
        value={form.getFieldValue(fieldName)}
        onChange={val => form.setFields([{ name: fieldName, value: val }])}
        {...props}
      />
    );
  }

  if (type === 'file' && form) {
    return (
      <FileField
        fileList={form.getFieldValue(fieldName) || []}
        onChange={info =>
          form.setFields([{ name: fieldName, value: info.fileList }])
        }
        {...props}
      />
    );
  }

  // Controlled AntD fields
  if (form) {
    switch (type) {
      case 'text':
      case 'email':
      case 'password':
        return (
          <InputField
            type={type}
            placeholder={placeholder}
            value={form.getFieldValue(fieldName) ?? ''}
            onChange={value => form.setFields([{ name: fieldName, value }])}
            {...props}
          />
        );
      case 'textarea':
        return (
          <TextAreaField
            rows={4}
            placeholder={placeholder}
            value={form.getFieldValue(fieldName) ?? ''}
            onChange={value => form.setFields([{ name: fieldName, value }])}
            {...props}
          />
        );
      case 'number':
        return (
          <NumberField
            placeholder={placeholder}
            value={form.getFieldValue(fieldName)}
            onChange={value => {
              form.setFields([{ name: fieldName, value }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(value, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            align={align}
            format={format}
            decimalPlaces={decimalPlaces}
            {...props}
          />
        );
      case 'select':
        return (
          <SelectField
            placeholder={placeholder}
            value={form.getFieldValue(fieldName)}
            onChange={value => {
              form.setFields([{ name: fieldName, value }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(value, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            options={options}
            searchable={searchable}
            status={
              form.getFieldError(fieldName).length > 0 ? 'error' : undefined
            }
            {...props}
          />
        );
      case 'multiselect':
        return (
          <SelectField
            mode="multiple"
            placeholder={placeholder}
            value={form.getFieldValue(fieldName) || []}
            onChange={value => form.setFields([{ name: fieldName, value }])}
            options={options}
            searchable={searchable}
            status={
              form.getFieldError(fieldName).length > 0 ? 'error' : undefined
            }
            {...props}
          />
        );
      case 'date':
        return (
          <DateTimePickerField
            value={form.getFieldValue(fieldName)}
            onChange={val => {
              form.setFields([{ name: fieldName, value: val }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(val, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            placeholder={placeholder}
            showTime={false}
            {...props}
          />
        );
      case 'datetime':
        return (
          <DateTimePickerField
            value={form.getFieldValue(fieldName)}
            onChange={val => {
              form.setFields([{ name: fieldName, value: val }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(val, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            placeholder={placeholder}
            showTime={true}
            {...props}
          />
        );
      case 'checkbox':
        return (
          <CheckboxField
            label={field.label}
            checked={!!form.getFieldValue(fieldName)}
            onChange={checked =>
              form.setFields([{ name: fieldName, value: checked }])
            }
            {...props}
          />
        );
      case 'switch':
        return (
          <SwitchField
            checked={!!form.getFieldValue(fieldName)}
            onChange={checked =>
              form.setFields([{ name: fieldName, value: checked }])
            }
            {...props}
          />
        );
      case 'radio':
        return (
          <RadioField
            value={form.getFieldValue(fieldName)}
            onChange={value => {
              form.setFields([{ name: fieldName, value }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(value, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            options={options}
            {...props}
          />
        );
      case 'richtext':
        return (
          <TextAreaField
            rows={6}
            placeholder={placeholder}
            value={form.getFieldValue(fieldName) ?? ''}
            onChange={value => form.setFields([{ name: fieldName, value }])}
            {...props}
          />
        );
      case 'telephone':
        return (
          <TelephoneField
            value={form.getFieldValue(fieldName) ?? ''}
            onChange={val => form.setFields([{ name: fieldName, value: val }])}
            placeholder={placeholder}
            {...props}
          />
        );
      case 'table':
        return (
          <TableField
            value={form.getFieldValue(fieldName) || []}
            onChange={val => form.setFields([{ name: fieldName, value: val }])}
            columns={field.tableProps?.columns || []}
            title={field.tableProps?.title}
            addButtonText={field.tableProps?.addButtonText}
            emptyText={field.tableProps?.emptyText}
            maxRows={field.tableProps?.maxRows}
            minRows={field.tableProps?.minRows}
            showRowNumbers={field.tableProps?.showRowNumbers}
            showAddButton={field.tableProps?.showAddButton}
            addButtonPosition={field.tableProps?.addButtonPosition}
            showActions={field.tableProps?.showActions}
            summaryRow={field.tableProps?.summaryRow}
            form={form}
            fieldName={
              Array.isArray(fieldName) ? fieldName.join('.') : fieldName
            }
            onSummaryChange={field.tableProps?.onSummaryChange}
            onRowDelete={field.tableProps?.onRowDelete}
            actionCallbacks={field.tableProps?.actionCallbacks}
            customActions={field.tableProps?.customActions}
            style={field.tableProps?.style}
            className={field.tableProps?.className}
            {...props}
          />
        );
      case 'resourceSelect':
        // Get field validation status
        const fieldErrors = form.getFieldError(fieldName);
        const hasError = fieldErrors.length > 0;
        const status = hasError ? 'error' : undefined;

        // Get the current field value or use defaultValue if not set
        const currentFieldValue = form.getFieldValue(fieldName);
        const initialValue =
          currentFieldValue !== undefined
            ? currentFieldValue
            : field.defaultValue;

        return (
          <SearchSelectField
            resource={field.searchSelectProps?.resource || ''}
            optionValue={field.searchSelectProps?.optionValue || 'id'}
            searchFields={
              field.searchSelectProps?.searchFields ||
              (field.searchSelectProps?.searchField
                ? [field.searchSelectProps.searchField]
                : ['name'])
            }
            searchDebounceMs={field.searchSelectProps?.searchDebounceMs || 300}
            filters={field.searchSelectProps?.filters || []}
            sorters={field.searchSelectProps?.sorters || []}
            pagination={
              field.searchSelectProps?.pagination
                ? {
                    pageSize: field.searchSelectProps.pagination.pageSize || 10,
                  }
                : { pageSize: 10 }
            }
            queryOptions={
              field.searchSelectProps?.queryOptions
                ? {
                    enabled:
                      field.searchSelectProps.queryOptions.enabled ?? true,
                    staleTime:
                      field.searchSelectProps.queryOptions.staleTime ??
                      5 * 60 * 1000,
                    cacheTime:
                      field.searchSelectProps.queryOptions.cacheTime ??
                      10 * 60 * 1000,
                    refetchOnWindowFocus:
                      field.searchSelectProps.queryOptions
                        .refetchOnWindowFocus ?? false,
                    refetchOnMount:
                      field.searchSelectProps.queryOptions.refetchOnMount ??
                      true,
                  }
                : {
                    enabled: true,
                    staleTime: 5 * 60 * 1000,
                    cacheTime: 10 * 60 * 1000,
                    refetchOnWindowFocus: false,
                    refetchOnMount: true,
                  }
            }
            optionRender={
              field.searchSelectProps?.optionRender ||
              ((option: any) => option.name || option.label || option.id)
            }
            autoSelectFirst={field.searchSelectProps?.autoSelectFirst || false}
            initialValue={initialValue}
            placeholder={placeholder}
            value={currentFieldValue}
            onChange={val => {
              form?.setFields([{ name: fieldName, value: val }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(val, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            required={field.required}
            status={status}
            allowClear={true}
            {...props}
          />
        );
      case 'resourceAutoComplete':
        // Get field validation status
        const autoCompleteFieldErrors = form.getFieldError(fieldName);
        const autoCompleteHasError = autoCompleteFieldErrors.length > 0;
        const autoCompleteStatus = autoCompleteHasError ? 'error' : undefined;

        return (
          <AutoCompleteField
            resource={field.autoCompleteProps?.resource || ''}
            optionValue={field.autoCompleteProps?.optionValue || 'id'}
            searchFields={
              field.autoCompleteProps?.searchFields ||
              (field.autoCompleteProps?.searchField
                ? [field.autoCompleteProps.searchField]
                : ['name'])
            }
            searchDebounceMs={field.autoCompleteProps?.searchDebounceMs || 300}
            filters={field.autoCompleteProps?.filters || []}
            sorters={field.autoCompleteProps?.sorters || []}
            pagination={
              field.autoCompleteProps?.pagination
                ? {
                    pageSize: field.autoCompleteProps.pagination.pageSize || 10,
                  }
                : { pageSize: 10 }
            }
            queryOptions={
              field.autoCompleteProps?.queryOptions
                ? {
                    enabled:
                      field.autoCompleteProps.queryOptions.enabled ?? true,
                    staleTime:
                      field.autoCompleteProps.queryOptions.staleTime ??
                      5 * 60 * 1000,
                    cacheTime:
                      field.autoCompleteProps.queryOptions.cacheTime ??
                      10 * 60 * 1000,
                    refetchOnWindowFocus:
                      field.autoCompleteProps.queryOptions
                        .refetchOnWindowFocus ?? false,
                    refetchOnMount:
                      field.autoCompleteProps.queryOptions.refetchOnMount ??
                      true,
                  }
                : {
                    enabled: true,
                    staleTime: 5 * 60 * 1000,
                    cacheTime: 10 * 60 * 1000,
                    refetchOnWindowFocus: false,
                    refetchOnMount: true,
                  }
            }
            optionRender={
              field.autoCompleteProps?.optionRender ||
              ((option: any) => option.name || option.label || option.id)
            }
            initialValue={form.getFieldValue(fieldName)}
            placeholder={placeholder}
            value={form.getFieldValue(fieldName)}
            onChange={val => {
              form?.setFields([{ name: fieldName, value: val }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(val, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            required={field.required}
            status={autoCompleteStatus}
            allowClear={true}
            {...props}
          />
        );
      case 'customAutoComplete':
        // Get field validation status
        const customAutoCompleteFieldErrors = form.getFieldError(fieldName);
        const customAutoCompleteHasError =
          customAutoCompleteFieldErrors.length > 0;
        const customAutoCompleteStatus = customAutoCompleteHasError
          ? 'error'
          : undefined;

        return (
          <CustomAutoCompleteField
            options={field.options || []}
            placeholder={placeholder}
            value={form.getFieldValue(fieldName)}
            onChange={val => {
              form?.setFields([{ name: fieldName, value: val }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(val, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            allowCustomInput={true}
            {...props}
          />
        );
      case 'searchTermTag':
        return (
          <SearchTermTagField
            value={form.getFieldValue(fieldName) ?? ''}
            onChange={value => {
              form.setFields([{ name: fieldName, value }]);

              if (field.onChange && typeof field.onChange === 'function') {
                try {
                  field.onChange(value, form);
                } catch (error) {
                  console.error('Error in field onChange callback:', error);
                }
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            maxTags={field.maxTags || 10}
            {...props}
          />
        );
      case 'customTitle':
        return field.render
          ? field.render(form.getFieldValue(fieldName), null, form)
          : null;
      case 'custom':
        if (field.render) {
          // For custom render functions, we need to handle dependencies
          if (field.dependencies && field.dependencies.length > 0) {
            // Get all dependency values
            const dependencyValues = field.dependencies.map((dep: string) => form.getFieldValue(dep));
            // Use dependency values as part of the render key to force re-render
            return field.render(form.getFieldValue(fieldName), null, form);
          }
          return field.render(form.getFieldValue(fieldName), null, form);
        }
        if (field.component) {
          const CustomComponent = field.component;
          return (
            <CustomComponent
              value={form.getFieldValue(fieldName)}
              onChange={(val: any) =>
                form.setFields([{ name: fieldName, value: val }])
              }
              placeholder={placeholder}
              {...props}
            />
          );
        }
        return <InputField type="text" placeholder={placeholder} {...props} />;
      default:
        return <InputField type="text" placeholder={placeholder} {...props} />;
    }
  }

  // Fallback: uncontrolled
  switch (type) {
    case 'text':
    case 'email':
    case 'password':
      return <InputField type="text" placeholder={placeholder} {...props} />;
    case 'textarea':
      return <TextAreaField rows={4} placeholder={placeholder} {...props} />;
    case 'number':
      return <NumberField placeholder={placeholder} {...props} />;
    case 'select':
      return (
        <SelectField
          placeholder={placeholder}
          options={options}
          searchable={searchable}
          {...props}
        />
      );
    case 'multiselect':
      return (
        <SelectField
          mode="multiple"
          placeholder={placeholder}
          options={options}
          searchable={searchable}
          {...props}
        />
      );
    case 'date':
      return (
        <DateTimePickerField
          placeholder={placeholder}
          showTime={false}
          {...props}
        />
      );
    case 'datetime':
      return (
        <DateTimePickerField
          placeholder={placeholder}
          showTime={true}
          {...props}
        />
      );
    case 'checkbox':
      return <CheckboxField label={field.label} {...props} />;
    case 'switch':
      return <SwitchField {...props} />;
    case 'radio':
      return <RadioField options={options} {...props} />;
    case 'markdown':
      return <MarkdownField {...props} />;
    case 'richtext':
      return <TextAreaField rows={6} placeholder={placeholder} {...props} />;
    case 'telephone':
      return <TelephoneField placeholder={placeholder} {...props} />;
    case 'searchTermTag':
      return <SearchTermTagField placeholder={placeholder} {...props} />;
    case 'customTitle':
      return field.render ? field.render(null, null, null) : null;
    case 'custom':
      if (field.component) {
        const CustomComponent = field.component;
        return <CustomComponent placeholder={placeholder} {...props} />;
      }
      return <InputField type="text" placeholder={placeholder} {...props} />;
    default:
      return <InputField type="text" placeholder={placeholder} {...props} />;
  }
};

// Export the validation utility for use in other components
export { getValidationRules, validateTableField, validateSearchSelectValue };
